import { Fragment, useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import {
  ISSUE_STOP_REASONS,
  type ConstraintAdjustmentRow,
  type LotAdjustmentRow,
  type RestrictionEntry,
  type StopReason,
  type SubstitutionEntry,
} from "../lib/explainTypes";
import LotDetailRows from "./LotDetailRows";
import { labelFor } from "./ConstrainOverview";

interface Props {
  title: string;
  symbols: string[];
  currentWeights: Record<string, string>;
  modelTarget: Record<string, string>;
  finalTarget: Record<string, string>;
  adjustments: ConstraintAdjustmentRow[];
  lotAdjustments: LotAdjustmentRow[];
  restrictions: RestrictionEntry[];
  substitutions: SubstitutionEntry[];
  isTaxable: boolean;
}

const dash = "—";

const num = (s: string | null | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

const asPercent = (s: string | null | undefined, decimals = 2): string =>
  s == null || s === "" ? dash : `${(num(s) * 100).toFixed(decimals)}%`;

const ISSUE_SET = new Set<StopReason>(ISSUE_STOP_REASONS);

function adjustmentLabel(
  symbol: string,
  restriction: RestrictionEntry | undefined,
  substitution: SubstitutionEntry | undefined,
  adjustments: ConstraintAdjustmentRow[],
): string {
  if (restriction) {
    switch (restriction.type) {
      case "hold_only":
        return "Hold only";
      case "exclude":
        return "Excluded";
      case "sell_no_tax":
        return "Sell no tax";
      case "like_primary":
        return substitution
          ? `Sub → ${substitution.primary}`
          : "Substituted (like primary)";
      default:
        return restriction.type;
    }
  }
  if (substitution) {
    return substitution.secondary === symbol
      ? `Sub → ${substitution.primary}`
      : `← ${substitution.secondary}`;
  }
  if (adjustments.length === 0) return dash;
  // Numeric delta (target_after − target_before) is what changed at the
  // constrain pass. Use the net across all rows for this symbol.
  const net = adjustments.reduce(
    (acc, a) => acc + (num(a.target_after) - num(a.target_before)),
    0,
  );
  const pp = net * 100;
  if (Math.abs(pp) < 0.005) return "0 pp";
  return `${pp > 0 ? "+" : ""}${pp.toFixed(2)} pp`;
}

// Reusable table for Overweight and Underweight/at-target groups in
// the Constrain section. One column per
// dc-constrain-section-ui-redesign-replace-019e1dc7 §1:
//   Sym  Cur%  Mdl%  Adjustment  Final%  Lots  Stop reason
//
// Restrictions / substitutions surface inline in the Adjustment
// column. Final% carries `(!)` when the constrained outcome differs
// from the model target because of an issue-class cap. Rows with
// lot detail are expandable in place; rows without lots are not
// expandable.
export default function SymbolConstraintTable({
  title,
  symbols,
  currentWeights,
  modelTarget,
  finalTarget,
  adjustments,
  lotAdjustments,
  restrictions,
  substitutions,
  isTaxable,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const indexes = useMemo(() => {
    const adjBySymbol = new Map<string, ConstraintAdjustmentRow[]>();
    for (const a of adjustments) {
      const arr = adjBySymbol.get(a.symbol) ?? [];
      arr.push(a);
      adjBySymbol.set(a.symbol, arr);
    }
    const lotsBySymbol = new Map<string, LotAdjustmentRow[]>();
    for (const l of lotAdjustments) {
      if (!l.symbol) continue;
      const arr = lotsBySymbol.get(l.symbol) ?? [];
      arr.push(l);
      lotsBySymbol.set(l.symbol, arr);
    }
    const restrictionBySymbol = new Map<string, RestrictionEntry>();
    for (const r of restrictions) restrictionBySymbol.set(r.symbol, r);
    const subsBySymbol = new Map<string, SubstitutionEntry>();
    for (const s of substitutions) {
      subsBySymbol.set(s.primary, s);
      subsBySymbol.set(s.secondary, s);
    }
    return { adjBySymbol, lotsBySymbol, restrictionBySymbol, subsBySymbol };
  }, [adjustments, lotAdjustments, restrictions, substitutions]);

  if (symbols.length === 0) {
    return (
      <div className="mb-3">
        <div className="fw-semibold mb-2">{title}</div>
        <div className="text-muted small">No symbols.</div>
      </div>
    );
  }

  const toggle = (sym: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });
  };

  return (
    <div className="mb-3">
      <div className="fw-semibold mb-2">{title}</div>
      <Table size="sm" className="mb-0">
        <thead className="text-muted">
          <tr>
            <th style={{ width: "8ch" }}>Sym</th>
            <th className="text-end">Cur%</th>
            <th className="text-end">Mdl%</th>
            <th>Adjustment</th>
            <th className="text-end">Final%</th>
            <th className="text-end">Lots</th>
            <th>Stop reason</th>
          </tr>
        </thead>
        <tbody>
          {symbols.map((sym) => {
            const adjs = indexes.adjBySymbol.get(sym) ?? [];
            const lots = indexes.lotsBySymbol.get(sym) ?? [];
            const restriction = indexes.restrictionBySymbol.get(sym);
            const substitution = indexes.subsBySymbol.get(sym);
            const stopReasons = Array.from(
              new Set(
                adjs
                  .map((a) => a.stop_reason)
                  .filter((r): r is StopReason => !!r),
              ),
            );
            const hasIssue = stopReasons.some((r) => ISSUE_SET.has(r));
            const stopReasonLabel =
              stopReasons.length === 0
                ? dash
                : stopReasons.map(labelFor).join(", ");
            // Per APP-1241 follow-up B: capped adjustments emit an
            // intended_target so the UI can show "intended X → Y" and
            // infer direction. Use the first non-null intended_target
            // from this symbol's adjustments.
            const intendedTargetRaw = adjs
              .map((a) => a.intended_target)
              .find((v) => v != null && v !== "");
            const finalNum = num(finalTarget[sym]);
            const intendedNum =
              intendedTargetRaw != null ? num(intendedTargetRaw) : null;
            const capDirection: "above" | "below" | null =
              hasIssue && intendedNum != null
                ? finalNum > intendedNum
                  ? "above"
                  : finalNum < intendedNum
                    ? "below"
                    : null
                : null;
            const capTitle =
              capDirection === "above"
                ? `Cap prevented further sell — intended ${asPercent(intendedTargetRaw)}`
                : capDirection === "below"
                  ? `Cap prevented further buy — intended ${asPercent(intendedTargetRaw)}`
                  : "Constrained by a cap";
            const isExpandable = lots.length > 0;
            const isOpen = expanded.has(sym);
            const adjText = adjustmentLabel(
              sym,
              restriction,
              substitution,
              adjs,
            );
            return (
              <Fragment key={sym}>
                <tr>
                  <td>
                    {isExpandable ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        aria-expanded={isOpen}
                        aria-controls={`lots-${sym}`}
                        onClick={() => toggle(sym)}
                      >
                        <span aria-hidden className="me-1">
                          {isOpen ? "▾" : "▸"}
                        </span>
                        {sym}
                      </Button>
                    ) : (
                      <span>{sym}</span>
                    )}
                  </td>
                  <td className="text-end">{asPercent(currentWeights[sym])}</td>
                  <td className="text-end">{asPercent(modelTarget[sym])}</td>
                  <td>{adjText}</td>
                  <td className="text-end">
                    {asPercent(finalTarget[sym])}
                    {hasIssue && (
                      <span
                        className="ms-1 text-danger"
                        title={capTitle}
                        aria-label="capped"
                      >
                        (!)
                      </span>
                    )}
                    {intendedNum != null &&
                      Math.abs(intendedNum - finalNum) > 0.0001 && (
                        <div className="small text-muted">
                          intended {asPercent(intendedTargetRaw)}
                        </div>
                      )}
                  </td>
                  <td className="text-end">
                    {lots.length === 0 ? dash : lots.length}
                  </td>
                  <td>{stopReasonLabel}</td>
                </tr>
                {isExpandable && isOpen && (
                  <tr id={`lots-${sym}`}>
                    <td colSpan={7} className="bg-light">
                      <LotDetailRows lots={lots} isTaxable={isTaxable} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
